/**
 * KAN-826: editable list of strings (phones, e-mails of a company): one input
 * per row, "Удалить" per row and "Добавить ..." under the list.
 * Read-only mode shows the inputs disabled without the buttons.
 */
import { Plus, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

interface StringListFieldProps {
  id: string;
  /** Group caption ("Телефоны"). */
  label: string;
  /** Row caption, numbered: "Телефон 1". */
  itemLabel: string;
  /** Text of the add button ("Добавить телефон"). */
  addLabel: string;
  values: string[];
  type?: 'text' | 'email' | 'tel';
  readOnly?: boolean;
  error?: string;
  onChange: (index: number, value: string) => void;
  onAdd: () => void;
  onRemove: (index: number) => void;
}

export function StringListField({
  id,
  label,
  itemLabel,
  addLabel,
  values,
  type = 'text',
  readOnly = false,
  error,
  onChange,
  onAdd,
  onRemove,
}: StringListFieldProps) {
  const errorId = `${id}-error`;
  return (
    <fieldset className="space-y-2">
      <legend className="text-sm font-medium leading-none">{label}</legend>
      {values.length === 0 && readOnly && <p className="text-sm text-gray-500">Не указаны</p>}
      {values.map((value, index) => {
        const rowLabel = `${itemLabel} ${index + 1}`;
        return (
          <div key={index} className="flex items-center gap-2">
            <Input
              type={type}
              aria-label={rowLabel}
              value={value}
              disabled={readOnly}
              aria-invalid={error ? true : undefined}
              aria-describedby={error ? errorId : undefined}
              className="min-w-0"
              onChange={(e) => onChange(index, e.target.value)}
            />
            {!readOnly && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-9 w-9 shrink-0"
                aria-label={`Удалить: ${rowLabel}`}
                title="Удалить"
                onClick={() => onRemove(index)}
              >
                <X className="h-4 w-4" aria-hidden="true" />
              </Button>
            )}
          </div>
        );
      })}
      {!readOnly && (
        <Button type="button" variant="outline" size="sm" onClick={onAdd}>
          <Plus className="mr-1 h-4 w-4" aria-hidden="true" />
          {addLabel}
        </Button>
      )}
      {error && (
        <p id={errorId} data-testid={`field-error-${id}`} className="text-sm text-red-600">
          {error}
        </p>
      )}
    </fieldset>
  );
}
